import React, { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { Col, TextButton } from '@opendoor/bricks-next';
import AddressInput from '@opendoor/cinderblocks/core/AddressInput';
import type {
  AddressInputProps,
  SelectedAddress as AddressInputSelectedAddress,
  AddressValidationResult,
  AutoCompletePrediction,
} from '@opendoor/cinderblocks/core/AddressInput';
import { globalObservability } from '@opendoor/observability/slim';
import type {
  OdProtosCasaData_Result,
  OdProtosSellReceptionData_SellerInput_Channel,
} from '__generated__/athena';
import { useObservability } from 'helpers/observability';
import useQueryParams from 'helpers/queryParams';
import { useRouter } from 'next/router';

import type { AddressSearchLocation, TrackingTaxonomy } from '../../components/globals';
import { AddressConfirmationModal } from '../../components/landing-pages-v2/AddressConfirmationModal';
import { useAddressValidationContext } from '../../helpers/AddressValidationContext';
import type { SelectedAddress } from '../../helpers/AddressValidationContext';

interface NewSellerFlowAddressInputProps {
  inputLocation: AddressSearchLocation;
  trackingTaxonomy: TrackingTaxonomy;
  analyticsPrefix?: string;
  placeholder: string;
  hideLabel?: boolean;
  pageSetPath?: string;
  trackingKeywords?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
  actionButton?: AddressInputProps['actionButton'];
  icon?: ReactNode;
  listBoxId?: AddressInputProps['listBoxId'];
  listBoxAriaLabel?: AddressInputProps['listBoxAriaLabel'];
  /**
   * Renders a CTA below the field by consuming AddressInput's single suffix action slot.
   * Takes precedence over actionButton when both are provided.
   */
  belowActionButton?: (props: {
    onPress: () => void;
    isLoading: boolean;
    isReady: boolean;
  }) => ReactNode;
  size?: AddressInputProps['size'];
}

interface BelowActionButtonRegistrationProps {
  isLoading?: boolean;
  onPress: () => void;
  onStateChange: (state: { isLoading: boolean; onPress: () => void }) => void;
}

const BelowActionButtonRegistration: React.FC<BelowActionButtonRegistrationProps> = ({
  isLoading,
  onPress,
  onStateChange,
}) => {
  // Cinderblocks exposes "submit the first address suggestion" through its suffix action hook.
  // Keep that adaptation in this shared wrapper so feature components can render an explicit below-field CTA.
  useEffect(() => {
    onStateChange({ isLoading: Boolean(isLoading), onPress });
  }, [isLoading, onPress, onStateChange]);

  return null;
};

const ManualEntryButton: React.FC<{
  onMouseDown: (e: React.MouseEvent) => void;
  onPress: () => void;
}> = ({ onMouseDown, onPress }) => {
  return (
    <TextButton
      onMouseDown={onMouseDown}
      label="Enter address manually instead"
      analyticsName="cosmos-address-input-manual-entry"
      type="button"
      onPress={onPress}
      width="100%"
      variant="primary"
      size="medium"
      px="$5"
      pt="$2half"
      pb="$5"
    />
  );
};

const NewSellerFlowAddressInput: React.FC<NewSellerFlowAddressInputProps> = ({
  actionButton,
  belowActionButton,
  channel,
  hideLabel,
  icon,
  inputLocation,
  listBoxAriaLabel = 'addresses',
  listBoxId = 'cb1-listbox',
  pageSetPath,
  placeholder,
  trackingKeywords,
  trackingTaxonomy,
  size = 'large',
}) => {
  const router = useRouter();
  const { trackJourneyEvent } = useObservability();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const belowActionButtonPressRef = React.useRef<() => void>(() => undefined);
  const [belowActionButtonState, setBelowActionButtonState] = useState({
    isLoading: false,
    isRegistered: false,
  });

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [predictions, setPredictions] = useState<AutoCompletePrediction[]>([]);

  const {
    validatedAddress,
    setValidatedAddress,
    setAddressResults,
    setFirstPrediction,
    setPartnerReferralId,
    setPageSetPath,
    setTrackingKeywords,
    setChannel,
  } = useAddressValidationContext();

  const { partner_referral } = useQueryParams();

  const handleBelowActionButtonStateChange = useCallback(
    ({ isLoading, onPress }: { isLoading: boolean; onPress: () => void }) => {
      belowActionButtonPressRef.current = onPress;
      setBelowActionButtonState((current) =>
        current.isRegistered && current.isLoading === isLoading
          ? current
          : { isLoading, isRegistered: true },
      );
    },
    [],
  );

  const handleBelowActionButtonPress = useCallback(() => {
    belowActionButtonPressRef.current();
  }, []);

  useEffect(() => {
    if (partner_referral) {
      setPartnerReferralId(partner_referral as string);
    }
    if (pageSetPath) {
      setPageSetPath(pageSetPath as string);
    }
    if (trackingKeywords) {
      setTrackingKeywords(trackingKeywords as string);
    }
    if (channel) {
      setChannel(channel as OdProtosSellReceptionData_SellerInput_Channel);
    }
  }, [partner_referral, pageSetPath, trackingKeywords, channel]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleManualEntryPress = useCallback(() => {
    router.push({
      pathname: '/manual-address-entry',
      query: {
        ...(trackingKeywords != null && { trackingKeywords }),
        ...(partner_referral != null && { partnerReferralId: partner_referral }),
        ...(pageSetPath != null && { pageSetPath }),
        ...(channel != null && { channel }),
      },
    });
  }, [router, trackingKeywords, partner_referral, pageSetPath, channel]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const handlePredictionsChange = (newPredictions: AutoCompletePrediction[]) => {
    setPredictions(newPredictions);
    if (newPredictions.length > 0) {
      setFirstPrediction(newPredictions[0]);
    }
  };

  const handleSearchTermChange = useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  }, []);

  const customAction =
    predictions.length < 5 && searchTerm !== '' && isFocused ? (
      <ManualEntryButton onMouseDown={handleMouseDown} onPress={handleManualEntryPress} />
    ) : undefined;
  const isBelowActionButtonReady =
    belowActionButtonState.isRegistered && searchTerm.trim() !== '' && predictions.length > 0;

  const resolvedActionButton = belowActionButton
    ? (onPress: () => void, isLoading?: boolean) => (
        <BelowActionButtonRegistration
          isLoading={isLoading}
          onPress={onPress}
          onStateChange={handleBelowActionButtonStateChange}
        />
      )
    : actionButton;

  const handleAddressValidated = async (validationResult: AddressValidationResult) => {
    const {
      address: validatedAddr,
      results,
      needsManualEntry,
      needsUnitSelection,
    } = validationResult;

    // Convert to SelectedAddress format for context
    const selectedAddress: SelectedAddress = {
      id: validatedAddr.id,
      street1: validatedAddr.street1,
      city: validatedAddr.city,
      state: validatedAddr.state,
      postal_code: validatedAddr.postal_code,
      unit: validatedAddr.unit,
      latitude: validatedAddr.latitude,
      longitude: validatedAddr.longitude,
      result: results as OdProtosCasaData_Result[],
      source: validatedAddr.source,
    };

    setAddressResults(results as OdProtosCasaData_Result[]);
    setValidatedAddress(selectedAddress);

    // Redirect to manual entry if needed
    if (needsManualEntry) {
      router.push({
        pathname: '/manual-address-entry',
        query: {
          address: JSON.stringify(selectedAddress),
          result: results,
          ...(partner_referral != null && { partnerReferralId: partner_referral as string }),
          ...(pageSetPath != null && { pageSetPath }),
          ...(trackingKeywords != null && { trackingKeywords }),
          ...(channel != null && { channel }),
        },
      });
      return;
    }

    // Show confirmation modal if unit selection is needed
    if (needsUnitSelection) {
      setIsConfirmationModalOpen(true);
      return;
    }

    setIsConfirmationModalOpen(true);
  };

  const handleError = (error: unknown) => {
    globalObservability.getSentryClient().captureException?.(error);
  };

  const handleAddressSelect = async (address: AddressInputSelectedAddress) => {
    try {
      // Track analytics - flush immediately since navigation follows shortly
      trackJourneyEvent(
        'sell',
        'addressEntryV2',
        {
          street1: address.street1,
          city: address.city,
          state: address.state,
          postalCode: address.postal_code,
          unit: address.unit,
          latitude: address.latitude,
          longitude: address.longitude,
          sourcePage: router.asPath,
          campaign: router.query?.utm_campaign as string,
        },
        { flush: true },
      );
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <Col flex={1} gap={belowActionButton ? '$3' : undefined}>
      <Col className="od-address-input">
        <Col position="relative" className="cta-input-form">
          <AddressInput
            id="home-address-input"
            label="Enter your home address"
            hideLabel={hideLabel}
            placeholder={placeholder}
            disabled={false}
            onAddressSelect={handleAddressSelect}
            onAddressValidated={handleAddressValidated}
            validateAddress
            onError={handleError}
            onPredictionsChange={handlePredictionsChange}
            onSearchTermChange={handleSearchTermChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            customAction={customAction}
            listBoxId={listBoxId}
            listBoxAriaLabel={listBoxAriaLabel}
            actionButton={resolvedActionButton}
            icon={icon}
            size={size}
          />
        </Col>
      </Col>
      {belowActionButton?.({
        onPress: handleBelowActionButtonPress,
        isLoading: belowActionButtonState.isLoading,
        isReady: isBelowActionButtonReady,
      })}
      {isConfirmationModalOpen && validatedAddress && (
        <AddressConfirmationModal
          setIsOpen={setIsConfirmationModalOpen}
          isOpen={isConfirmationModalOpen}
          partnerReferralId={partner_referral as string}
          initialAddress={validatedAddress}
          pageSetPath={pageSetPath}
          trackingKeywords={trackingKeywords}
          channel={channel}
          inputLocation={inputLocation}
          trackingTaxonomy={trackingTaxonomy}
        />
      )}
    </Col>
  );
};

export default NewSellerFlowAddressInput;
